import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/format";
import { hasFunctionalConsent } from "@/lib/consent";
import { MfaChallenge } from "@/components/MfaChallenge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, GraduationCap, ArrowLeft, KeyRound } from "lucide-react";

const getCookie = (k) => document.cookie.split("; ").find((c) => c.startsWith(k + "="))?.split("=")[1];
const setCookie = (k, v) => { document.cookie = `${k}=${encodeURIComponent(v)}; path=/; max-age=${60 * 60 * 24 * 365}`; };

export default function Login() {
    const { login, veracrossLogin } = useAuth();
    const navigate = useNavigate();
    const [route, setRoute] = useState("");
    const [vEmail, setVEmail] = useState("");
    const [vPass, setVPass] = useState("");
    const [email, setEmail] = useState("");
    const [pass, setPass] = useState("");
    const [busy, setBusy] = useState(false);
    const [challenge, setChallenge] = useState(null);

    useEffect(() => { const c = getCookie("pe_school_route"); if (c) setRoute(decodeURIComponent(c)); }, []);

    const doLocal = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            const res = await login(email, pass);
            if (res.mfa) setChallenge(res);
            else { toast.success("Welcome back"); navigate("/app"); }
        } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Login failed"); }
        finally { setBusy(false); }
    };
    const doVeracross = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            if (hasFunctionalConsent()) setCookie("pe_school_route", route.trim());
            const res = await veracrossLogin(route.trim(), vEmail, vPass);
            if (res.mfa) setChallenge(res);
            else { toast.success("Signed in with Veracross"); navigate("/app"); }
        } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Login failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-emerald-50 text-brand-emerald p-12 relative overflow-hidden">
                <div className="relative z-10"><img src="/procureed-logo.png" alt="ProcureEd" className="h-12 w-auto" /></div>
                <div className="relative z-10 max-w-md">
                    <h2 className="text-3xl font-semibold tracking-tight leading-tight">Purchasing & AP automation, integrated with Veracross.</h2>
                    <p className="mt-4 text-slate-600">Admins sign in with email & password and 2-step verification. Faculty approvers use Veracross SSO.</p>
                </div>
                <div className="relative z-10 flex items-center gap-3 text-sm">
                    <img src="/veracross-logo.png" alt="Veracross" className="h-6 w-auto" />
                    <span className="text-muted-foreground">Veracross Integration Partner</span>
                </div>
                <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-brand-teal/10 blur-3xl" />
            </div>

            <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-brand-bone">
                <div className="w-full max-w-md mx-auto">
                    <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald mb-8" data-testid="back-home-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="lg:hidden mb-6"><img src="/procureed-logo.png" alt="ProcureEd" className="h-11 w-auto" /></div>

                    {challenge ? (
                        <>
                            <h1 className="text-2xl font-semibold tracking-tight text-brand-emerald mb-4">Verify it's you</h1>
                            <MfaChallenge challenge={challenge} onComplete={() => { toast.success("Signed in"); navigate("/app"); }} />
                        </>
                    ) : (
                        <>
                            <h1 className="text-2xl font-semibold tracking-tight text-brand-emerald">School sign in</h1>
                            <p className="text-sm text-muted-foreground mt-1">Choose how you'd like to access ProcureEd.</p>

                            <Tabs defaultValue="email" className="mt-6">
                                <TabsList className="grid grid-cols-2 w-full">
                                    <TabsTrigger value="email" data-testid="tab-email"><KeyRound className="h-4 w-4 mr-1.5" /> Email & password</TabsTrigger>
                                    <TabsTrigger value="veracross" data-testid="tab-veracross"><GraduationCap className="h-4 w-4 mr-1.5" /> Veracross SSO</TabsTrigger>
                                </TabsList>

                                <TabsContent value="email">
                                    <form onSubmit={doLocal} className="space-y-4 mt-4 bg-white border border-border rounded-xl p-6">
                                        <div>
                                            <Label htmlFor="e-email">Email</Label>
                                            <Input id="e-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" className="mt-1.5" data-testid="email-input" required />
                                        </div>
                                        <div>
                                            <Label htmlFor="e-pass">Password</Label>
                                            <Input id="e-pass" type="password" value={pass} onChange={(e) => setPass(e.target.value)} className="mt-1.5" data-testid="password-input" required />
                                        </div>
                                        <Button type="submit" disabled={busy} className="w-full bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="login-button">
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                                        </Button>
                                        <p className="text-[11px] text-muted-foreground text-center">School & Purchasing Admins (2-step verification required). Demo: business.manager@stjude.edu · apclerk@stjude.edu · <span className="font-mono">Veracross123</span></p>
                                    </form>
                                </TabsContent>

                                <TabsContent value="veracross">
                                    <form onSubmit={doVeracross} className="space-y-4 mt-4 bg-white border border-border rounded-xl p-6">
                                        <div>
                                            <Label htmlFor="v-route">Veracross school route</Label>
                                            <Input id="v-route" value={route} onChange={(e) => setRoute(e.target.value)} placeholder="e.g. stjude" className="mt-1.5 font-mono" data-testid="school-route-input" required />
                                            <p className="text-[11px] text-muted-foreground mt-1">Remembered on this device.</p>
                                        </div>
                                        <div>
                                            <Label htmlFor="v-email">Veracross email</Label>
                                            <Input id="v-email" type="email" value={vEmail} onChange={(e) => setVEmail(e.target.value)} placeholder="you@school.edu" className="mt-1.5" data-testid="veracross-email-input" required />
                                        </div>
                                        <div>
                                            <Label htmlFor="v-pass">Password</Label>
                                            <Input id="v-pass" type="password" value={vPass} onChange={(e) => setVPass(e.target.value)} className="mt-1.5" data-testid="veracross-password-input" required />
                                        </div>
                                        <Button type="submit" disabled={busy} className="w-full bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="veracross-login-button">
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in with Veracross"}
                                        </Button>
                                        <p className="text-[11px] text-muted-foreground text-center">Requesters & Approvers. Demo: teacher@stjude.edu · depthead@stjude.edu · <span className="font-mono">Veracross123</span></p>
                                    </form>
                                </TabsContent>
                            </Tabs>

                            <div className="mt-6 text-sm">
                                <Link to="/signup" className="text-brand-teal hover:underline" data-testid="to-signup-link">New school? Create an account →</Link>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
